// src/pages/channel-order/EditOrderModal.jsx
// @ts-nocheck
import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import { API } from "../../utils/apiUrls"

const loaderSrc = '/animations/loader.lottie'

// Icons
const Icon = {
  X: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M18 6L6 18M6 6l12 12" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  Check: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M20 6L9 17l-5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  ChevronDown: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M6 9l6 6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  Package: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" strokeWidth="2"/>
      <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  MapPin: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" strokeWidth="2"/>
      <circle cx="12" cy="10" r="3" strokeWidth="2"/>
    </svg>
  ),
  FileText: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" strokeWidth="2"/>
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
};

/**
 * Simple deep merge: overlay `source` onto `target` recursively.
 * Arrays in source replace; plain objects are merged.
 */
function deepMerge(target = {}, source = {}) {
  if (typeof target !== 'object' || target === null) return source
  if (typeof source !== 'object' || source === null) return source

  const result = Array.isArray(target) ? [...target] : { ...target }

  Object.entries(source).forEach(([key, val]) => {
    if (Array.isArray(val)) {
      result[key] = [...val]
    } else if (typeof val === 'object' && val !== null) {
      result[key] = deepMerge(result[key], val)
    } else {
      result[key] = val
    }
  })

  return result
}

/**
 * Ensure the order object has minimum shape so UI doesn't blow up.
 * Handles both old field names (pickup_details, drop_details, shipment_details)
 * and new field names (market_place_pickup_details, market_place_customer_drop_details, market_place_shipment_details)
 */
function normalizeOrder(o = {}) {
  const clone = JSON.parse(JSON.stringify(o))
  
  clone.pickup_details = clone.market_place_pickup_details || clone.pickup_details || null
  clone.drop_details = clone.market_place_customer_drop_details || clone.drop_details || {}
  clone.shipment_details = clone.market_place_shipment_details || clone.shipment_details || {}

  // Ensure arrays exist
  clone.shipment_details.shipment_packages = Array.isArray(clone.shipment_details.shipment_packages)
    ? clone.shipment_details.shipment_packages
    : []

  // NEW: normalize fulfilled packages array
  clone.shipment_details.shipment_fulfilled_packages = Array.isArray(
    clone.shipment_details.shipment_fulfilled_packages
  )
    ? clone.shipment_details.shipment_fulfilled_packages
    : []

  // Guarantee at least one package with items for editing convenience
  if (clone.shipment_details.shipment_packages.length === 0) {
    clone.shipment_details.shipment_packages = [
      {
        waybill: null,
        dimensions: {
          unit: '',
          product_length: '',
          product_width: '',
          product_height: '',
        },
        weight: {
          unit: '',
          product_weight: '',
        },
        items: [],
        label_details: null,
        special_handling_details: null,
      },
    ]
  }

  // Ensure each package has items array and defaults
  clone.shipment_details.shipment_packages = clone.shipment_details.shipment_packages.map(pkg => {
    pkg.items = Array.isArray(pkg.items) ? pkg.items : []
    pkg.items = pkg.items.map(it => {
      it.item_payment_details = it.item_payment_details || {}
      if (!it.item_payment_details.currency) {
        it.item_payment_details.currency = 'INR'
      }
      return it
    })
    return pkg
  })

  if (clone.shipment_details?.order_details) {
    const od = clone.shipment_details.order_details
    od.order_invoice_details = od.order_invoice_details || {}
    if (!od.order_invoice_details.invoice_currency) {
      od.order_invoice_details.invoice_currency = 'INR'
    }

    const ca = od.cod_amount
    if (!ca || typeof ca !== 'object' || Array.isArray(ca)) {
      od.cod_amount = {
        value: ca == null ? '' : String(ca),
        currency: 'INR',
      }
    } else {
      if (od.cod_amount.value === undefined) od.cod_amount.value = ''
      od.cod_amount.currency = 'INR'
    }
  }

  return clone
}

export default function EditOrderModal({
  order,
  onClose,
  onSave, // optional override
  pickupList = [],
}) {
  const navigate = useNavigate()
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState('order') // 'order' | 'pickup' | 'drop' | 'packages'
  const [expandedPackages, setExpandedPackages] = useState(new Set([0]))
  const [localPickups, setLocalPickups] = useState([])
  const [loadingPickups, setLoadingPickups] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const effectivePickupList = pickupList && pickupList.length ? pickupList : localPickups

  // Initialize form copy from incoming order
  useEffect(() => {
    if (!order) return
    const normalized = normalizeOrder(order)
    setForm(prev => {
      // Only initialize if form is empty or order ID changed (preserves user edits)
      if (!prev.id || prev.id !== normalized.id) {
        return normalized
      }
      return prev
    })
  }, [order?.id])

  // Fetch pickup details if none passed
  useEffect(() => {
    if (effectivePickupList.length > 0) return
    const fetch = async () => {
      setLoadingPickups(true)
      try {
        const parentClientId =
          localStorage.getItem('parentClientId') ||
          localStorage.getItem('merchantId') ||
          ''
        if (!parentClientId) throw new Error('parentClientId missing in localStorage')

        const jwt = localStorage.getItem('authToken') || ''
        const url = API.getPickupDetails(parentClientId)
        const res = await axios.get(url, {
          headers: {
            Authorization: jwt,
          },
        })
        const pickups = Array.isArray(res.data)
          ? res.data
          : res.data.pickup_details || []
        setLocalPickups(pickups)
      } catch (err) {
        console.error('Failed to fetch pickup details:', err)
      } finally {
        setLoadingPickups(false)
      }
    }
    fetch()
  }, [effectivePickupList.length])

  // Generic change handler for inputs that map to top-level or nested snake_case names
  const handleChange = e => {
    const { name, value, type, checked } = e.target
    const val = type === 'checkbox' ? checked : value

    setForm(prev => {
      const nxt = JSON.parse(JSON.stringify(prev))
      if (name.includes('.')) {
        const keys = name.split('.')
        let cur = nxt
        for (let i = 0; i < keys.length - 1; i++) {
          const k = keys[i]
          cur[k] = cur[k] || {}
          cur = cur[k]
        }
        cur[keys[keys.length - 1]] = val
      } else {
        nxt[name] = val
      }
      return nxt
    })
  }

  // When user selects a saved pickup (for top-level pickup_details)
  const handlePickupSelect = e => {
    const selectedId = e.target.value
    const selected =
      effectivePickupList.find(p => p.pickup_address_id === selectedId) || null
    setForm(prev => {
      const nxt = JSON.parse(JSON.stringify(prev))
      if (selected) {
        nxt.pickup_details = {
          pickup_poc: selected.pickup_poc || '',
          pickup_organisation: selected.pickup_organisation || '',
          pickup_address: selected.pickup_address || '',
          pickup_district: selected.pickup_district || '',
          pickup_city: selected.pickup_city || '',
          pickup_state: selected.pickup_state || '',
          pickup_landmark: selected.pickup_landmark || '',
          pickup_poc_email: selected.pickup_poc_email || '',
          pickup_poc_contact_number: selected.pickup_poc_contact_number || '',
          pickup_postal_code: selected.pickup_postal_code || '',
          pickup_country_code: selected.pickup_country_code || '',
          pickup_address_type: selected.pickup_address_type || '',
          user_defined_additional_data: Array.isArray(
            selected.user_defined_additional_data
          )
            ? selected.user_defined_additional_data
            : [],
          // UI-only, will be stripped before save
          pickup_address_id: selected.pickup_address_id,
        }
      } else {
        nxt.pickup_details = null
      }
      return nxt
    })
  }

  const handleClose = () => {
    setIsClosing(true)
    setTimeout(() => {
      onClose(false)
    }, 200)
  }

  // Submit handler: deep merge original `order` with `form` (which is the edited copy) and send full payload
  const handleSubmit = async () => {
    if (!order) return
    
    // Validate required fields
    const errors = []
    
    // Validate pickup details if present
    if (form.pickup_details) {
      const pd = form.pickup_details
      if (!pd.pickup_poc?.trim()) errors.push('Pickup Contact Person is required')
      if (!pd.pickup_address?.trim()) errors.push('Pickup Address is required')
      if (!pd.pickup_city?.trim()) errors.push('Pickup City is required')
      if (!pd.pickup_state?.trim()) errors.push('Pickup State is required')
      if (!pd.pickup_poc_email?.trim()) errors.push('Pickup Email is required')
      if (!pd.pickup_poc_contact_number?.trim()) errors.push('Pickup Phone is required')
      if (!pd.pickup_postal_code?.trim()) errors.push('Pickup Postal Code is required')
    }
    
    // Validate drop details
    if (form.drop_details) {
      const dd = form.drop_details
      if (!dd.drop_poc?.trim()) errors.push('Delivery Contact Person is required')
      if (!dd.drop_address?.trim()) errors.push('Delivery Address is required')
      if (!dd.drop_city?.trim()) errors.push('Delivery City is required')
      if (!dd.drop_state?.trim()) errors.push('Delivery State is required')
      if (!dd.drop_poc_contact_number?.trim()) errors.push('Delivery Phone is required')
      if (!dd.drop_postal_code?.trim()) errors.push('Delivery Postal Code is required')
    }
    
    // Validate packages
    const packages = form.shipment_details?.shipment_packages || []
    packages.forEach((pkg, idx) => {
      // Validate dimensions
      const dims = pkg.dimensions || {}
      if (!dims.product_length || Number(dims.product_length) <= 0) {
        errors.push(`Package #${idx + 1}: Length must be greater than 0`)
      }
      if (!dims.product_width || Number(dims.product_width) <= 0) {
        errors.push(`Package #${idx + 1}: Width must be greater than 0`)
      }
      if (!dims.product_height || Number(dims.product_height) <= 0) {
        errors.push(`Package #${idx + 1}: Height must be greater than 0`)
      }
      
      // Validate weight
      if (!pkg.weight?.product_weight || Number(pkg.weight.product_weight) <= 0) {
        errors.push(`Package #${idx + 1}: Weight must be greater than 0`)
      }
      
      // Validate items
      const items = pkg.items || []
      items.forEach((item, jdx) => {
        if (!item.item_general_details?.item_name?.trim()) {
          errors.push(`Package #${idx + 1}, Item #${jdx + 1}: Item Name is required`)
        }
        if (!item.quantity || Number(item.quantity) <= 0) {
          errors.push(`Package #${idx + 1}, Item #${jdx + 1}: Quantity must be greater than 0`)
        }
        if (!item.item_payment_details?.unit_price || Number(item.item_payment_details.unit_price) <= 0) {
          errors.push(`Package #${idx + 1}, Item #${jdx + 1}: Unit Price must be greater than 0`)
        }
      })
    })
    
    // If there are validation errors, show them and stop
    if (errors.length > 0) {
      alert(
        'Please fix the following validation errors:\n\n' +
        errors.map((err, i) => `${i + 1}. ${err}`).join('\n')
      )
      return
    }
    
    setSaving(true)
    try {
      // Start with a copy of the original order to preserve all fields
      const finalPayload = JSON.parse(JSON.stringify(order))
      
      // Form uses old field names (pickup_details, drop_details, shipment_details)
      // API expects new field names (market_place_pickup_details, market_place_customer_drop_details, market_place_shipment_details)
      // Always use form values - form is the source of truth for all user edits
      
      // Apply pickup details from form
      if (form.pickup_details !== undefined) {
        if (form.pickup_details === null) {
          finalPayload.market_place_pickup_details = null
        } else {
          finalPayload.market_place_pickup_details = JSON.parse(JSON.stringify(form.pickup_details))
          if (finalPayload.market_place_pickup_details.pickup_address_id) {
            delete finalPayload.market_place_pickup_details.pickup_address_id
          }
        }
      }
      
      // Apply drop details from form
      if (form.drop_details !== undefined) {
        finalPayload.market_place_customer_drop_details = JSON.parse(JSON.stringify(form.drop_details))
      }
      
      // Apply shipment details from form
      if (form.shipment_details !== undefined) {
        finalPayload.market_place_shipment_details = JSON.parse(JSON.stringify(form.shipment_details))
      }
      
      // Update other top-level fields from form
      // Exclude both old and new field names since we've already set them above
      const excludedKeys = [
        'pickup_details', 'drop_details', 'shipment_details',
        'market_place_pickup_details', 'market_place_customer_drop_details', 'market_place_shipment_details'
      ]
      Object.keys(form).forEach(key => {
        if (!excludedKeys.includes(key)) {
          if (typeof form[key] === 'object' && form[key] !== null && !Array.isArray(form[key])) {
            finalPayload[key] = JSON.parse(JSON.stringify(form[key]))
          } else {
            finalPayload[key] = form[key]
          }
        }
      })

      // Remove fields that shouldn't be sent
      if ('order_status' in finalPayload) {
        delete finalPayload.order_status
      }
      
      // Remove old field names - API should only receive new field names
      delete finalPayload.pickup_details
      delete finalPayload.drop_details
      delete finalPayload.shipment_details

      // Process order details for COD amount
      const od = finalPayload?.market_place_shipment_details?.order_details
      if (od) {
        const isCOD = String(od.order_type || '').toUpperCase() === 'COD'
        if (isCOD) {
          if (!od.cod_amount) od.cod_amount = {}
          const raw = od.cod_amount.value
          od.cod_amount.value = raw === '' || raw == null ? null : Number(raw)
          od.cod_amount.currency = 'INR'
        } else if ('cod_amount' in od) {
          delete od.cod_amount
        }
      }

      if (onSave) {
        await onSave(finalPayload)
      } else {
        const jwt = localStorage.getItem('authToken') || ''
        await axios.put(API.updateMarketplaceOrders(), finalPayload, {
          headers: {
            Authorization: jwt,
            'Content-Type': 'application/json',
          },
        })
        handleClose()
      }
    } catch (err) {
      console.error('Update failed', err)
      alert(
        'Update failed: ' +
          (err?.response?.data?.message || err?.message || 'unknown error')
      )
    } finally {
      setSaving(false)
    }
  }

  const togglePackage = (idx) => {
    setExpandedPackages(prev => {
      const next = new Set(prev)
      next.has(idx) ? next.delete(idx) : next.add(idx)
      return next
    })
  }

  // Shortcut refs for rendering
  const pd = form.pickup_details || {}
  const dd = form.drop_details || {}
  const sd = form.shipment_details || {}
  const pkgs = Array.isArray(sd.shipment_packages) ? sd.shipment_packages : []

  const tabs = [
    { id: 'order', label: 'Order Info', icon: Icon.FileText },
    { id: 'pickup', label: 'Pickup', icon: Icon.MapPin },
    { id: 'drop', label: 'Delivery', icon: Icon.MapPin },
    { id: 'packages', label: 'Packages', icon: Icon.Package },
  ]

  const modalContent = (
    <div 
      className="fixed inset-0 flex items-start md:items-center justify-center p-4 transition-all duration-300"
      style={{
        zIndex: 99999,
        backdropFilter: isClosing ? 'blur(0px)' : 'blur(20px)',
        WebkitBackdropFilter: isClosing ? 'blur(0px)' : 'blur(20px)',
        backgroundColor: isClosing ? 'rgba(0, 0, 0, 0)' : 'rgba(0, 0, 0, 0.7)',
      }}
      onClick={handleClose}
    >
      <div 
        className={`bg-white rounded-3xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col transition-all duration-500 ease-out ${
          isClosing 
            ? 'scale-90 opacity-0 translate-y-12' 
            : 'scale-100 opacity-100 translate-y-0'
        }`}
        style={{
          zIndex: 100000,
          position: 'relative',
          boxShadow: isClosing 
            ? 'none' 
            : '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 100px rgba(99, 102, 241, 0.15)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Premium Enhanced Header with Animated Gradient */}
        <div className="relative bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 px-8 py-7 border-b border-white/10 overflow-hidden">
          {/* Animated gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent animate-gradient"></div>
          
          {/* Subtle animated shine effect */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
          </div>
          
          <div className="relative flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-2">
                <div className="relative group">
                  <div className="absolute inset-0 bg-white/30 rounded-xl blur-md group-hover:blur-lg transition-all"></div>
                  <div className="relative w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center ring-2 ring-white/40 group-hover:ring-white/60 transition-all group-hover:scale-105">
                    <Icon.FileText className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-2xl font-bold text-white tracking-tight">
                      Edit Order
                    </h2>
                    <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-semibold ring-1 ring-white/30">
                      #{form.order_number}
                    </span>
                  </div>
                  <p className="text-indigo-100 text-sm flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                    Update order details, addresses, and packages
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="group relative w-11 h-11 rounded-xl bg-white/10 hover:bg-white/20 active:bg-white/30 backdrop-blur-sm flex items-center justify-center transition-all duration-200 ring-1 ring-white/20 hover:ring-white/40 text-white hover:scale-105 active:scale-95"
              aria-label="Close modal"
              title="Close (Esc)"
            >
              <Icon.X className="h-5 w-5 group-hover:rotate-90 transition-transform duration-200" />
            </button>
          </div>
        </div>

        {/* Premium Enhanced Tabs */}
        <div className="relative bg-gradient-to-b from-slate-50 to-white border-b border-slate-200/80 px-8 pt-5 shadow-sm">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide -mb-px">
            {tabs.map((tab, idx) => {
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`group relative flex items-center gap-2.5 px-6 py-3.5 rounded-t-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap ${
                    isActive
                      ? 'bg-white text-indigo-600 shadow-lg ring-2 ring-indigo-100 -mb-px z-10'
                      : 'bg-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-100/70'
                  }`}
                  style={{
                    transform: isActive ? 'translateY(0)' : 'translateY(0)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  {/* Active indicator bar */}
                  {isActive && (
                    <div className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-indigo-500 to-violet-500 animate-slideIn"></div>
                  )}
                  
                  {/* Icon with animation */}
                  <div className={`relative transition-all duration-200 ${
                    isActive ? 'scale-110' : 'scale-100 group-hover:scale-105'
                  }`}>
                    <tab.icon className={`h-4 w-4 transition-colors duration-200 ${
                      isActive ? 'text-indigo-600' : 'text-slate-500 group-hover:text-slate-700'
                    }`} />
                    {isActive && (
                      <div className="absolute inset-0 bg-indigo-500 blur-md opacity-30 animate-pulse"></div>
                    )}
                  </div>
                  
                  {/* Label */}
                  <span className="relative">
                    {tab.label}
                  </span>
                  
                  {/* Hover glow effect */}
                  {!isActive && (
                    <div className="absolute inset-0 rounded-t-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gradient-to-b from-slate-100/50 to-transparent pointer-events-none"></div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Body with premium scrollbar and smooth transitions */}
        <div className="overflow-y-auto flex-1 p-8 bg-gradient-to-b from-slate-50/50 to-white premium-scrollbar">
          {/* Order Info Tab */}
          {activeTab === 'order' && (
            <div className="animate-fadeIn space-y-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Icon.FileText className="h-4 w-4 text-indigo-600" />
                  </span>
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    ['merchant_id', 'Merchant ID'],
                    ['order_number', 'Order Number'],
                    ['store_id', 'Store ID'],
                    ['market_place_channel_name', 'Channel'],
                  ].map(([k, lbl]) => (
                    <div key={k} className="group">
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2.5 flex items-center gap-2">
                        {lbl}
                        <span className="text-slate-400 text-[10px] font-normal normal-case">(Required)</span>
                      </label>
                      <div className="relative">
                        <input
                          name={k}
                          value={form[k] || ''}
                          onChange={handleChange}
                          className="w-full px-4 py-3.5 rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-400 hover:ring-slate-300 outline-none bg-white transition-all duration-200 font-medium text-slate-900 placeholder:text-slate-400 group-hover:shadow-sm focus:shadow-md"
                          placeholder={`Enter ${lbl.toLowerCase()}`}
                        />
                        <div className="absolute inset-0 rounded-xl opacity-0 group-focus-within:opacity-100 pointer-events-none transition-opacity duration-200 ring-2 ring-indigo-500/20 ring-offset-2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                    <Icon.Package className="h-4 w-4 text-violet-600" />
                  </span>
                  Shipment & Payment Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Shipment Type</label>
                    <div className="relative">
                      <select
                        name="shipment_order_type"
                        value={form.shipment_order_type || ''}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-300 hover:ring-slate-300 outline-none bg-white transition-all font-medium appearance-none cursor-pointer"
                      >
                        <option value="">—</option>
                        <option value="FORWARD">FORWARD</option>
                        <option value="REVERSE">REVERSE</option>
                      </select>
                      <Icon.ChevronDown className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                  </div>

                  {[
                    { path: 'order_reference_number', label: 'Reference #', type: 'text' },
                    { path: 'order_type', label: 'Order Type', type: 'text' },
                    { path: 'order_invoice_details.invoice_number', label: 'Invoice #', type: 'text' },
                    { path: 'order_invoice_details.invoice_value', label: 'Invoice Value', type: 'number' },
                    { path: 'order_invoice_details.invoice_currency', label: 'Currency', type: 'text' },
                  ].map(({ path, label, type }) => {
                    // Navigate from order_details level
                    const keys = path.split('.')
                    let val = form.shipment_details?.order_details
                    keys.forEach(k => {
                      val = val?.[k]
                    })
                    return (
                      <div key={path}>
                        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">{label}</label>
                        <input
                          type={type}
                          name={`shipment_details.order_details.${path}`}
                          value={val ?? ''}
                          onChange={handleChange}
                          {...(type === 'number' ? { min: '0', step: '0.01' } : {})}
                          className="w-full px-4 py-3 rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-300 hover:ring-slate-300 outline-none bg-white transition-all font-medium"
                        />
                      </div>
                    )
                  })}

                  {String(form?.shipment_details?.order_details?.order_type || '').toUpperCase() === 'COD' && (
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">COD Amount</label>
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        name="shipment_details.order_details.cod_amount.value"
                        value={form?.shipment_details?.order_details?.cod_amount?.value ?? ''}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-300 hover:ring-slate-300 outline-none bg-white transition-all font-medium"
                        placeholder="Enter amount to collect"
                      />
                    </div>
                  )}
                </div>

                <label className="inline-flex items-center mt-4 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="process_shipment_order_in_async"
                    checked={!!form.process_shipment_order_in_async}
                    onChange={handleChange}
                    className="w-5 h-5 rounded border-2 border-slate-300 text-indigo-600 focus:ring-2 focus:ring-indigo-300 cursor-pointer"
                  />
                  <span className="ml-3 text-sm font-semibold text-slate-700 group-hover:text-slate-900 transition-colors">Process Asynchronously</span>
                </label>
              </div>
            </div>
          )}

          {/* Pickup Tab */}
          {activeTab === 'pickup' && (
            <div className="animate-fadeIn space-y-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Icon.MapPin className="h-4 w-4 text-emerald-600" />
                  </span>
                  Pickup Location
                </h3>
                
                <div className="mb-4">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                    Choose Saved Location
                  </label>
                  <div className="relative">
                    <select
                      className="w-full px-4 py-3 rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-300 hover:ring-slate-300 outline-none bg-white transition-all font-medium appearance-none cursor-pointer"
                      value={pd.pickup_address_id || ''}
                      onChange={handlePickupSelect}
                      disabled={loadingPickups}
                    >
                      <option value="">— Select Pickup Location —</option>
                      {effectivePickupList.map(p => (
                        <option
                          key={p.pickup_address_id}
                          value={p.pickup_address_id}
                        >
                          {`${p.pickup_address}, ${p.pickup_city}, ${p.pickup_state} (${p.pickup_organisation})`}
                        </option>
                      ))}
                    </select>
                    <Icon.ChevronDown className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { prop: 'pickup_poc', label: 'Contact Person', required: true, type: 'text' },
                    { prop: 'pickup_organisation', label: 'Organisation', required: false, type: 'text' },
                    { prop: 'pickup_address', label: 'Address', required: true, type: 'text' },
                    { prop: 'pickup_district', label: 'District', required: false, type: 'text' },
                    { prop: 'pickup_city', label: 'City', required: true, type: 'text' },
                    { prop: 'pickup_state', label: 'State', required: true, type: 'text' },
                    { prop: 'pickup_landmark', label: 'Landmark', required: false, type: 'text' },
                    { prop: 'pickup_poc_email', label: 'Email', required: true, type: 'email' },
                    { prop: 'pickup_poc_contact_number', label: 'Phone', required: true, type: 'tel' },
                    { prop: 'pickup_postal_code', label: 'Postal Code', required: true, type: 'text' },
                    { prop: 'pickup_country_code', label: 'Country Code', required: false, type: 'text' },
                    { prop: 'pickup_address_type', label: 'Address Type', required: false, type: 'text' },
                  ].map(({ prop, label, required, type }) => (
                    <div key={prop}>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                        {label}
                        {required && <span className="text-rose-600 ml-1">*</span>}
                      </label>
                      <input
                        type={type}
                        name={`pickup_details.${prop}`}
                        value={pd[prop] || ''}
                        onChange={handleChange}
                        required={required}
                        className="w-full px-4 py-3 rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-300 hover:ring-slate-300 outline-none bg-white transition-all font-medium"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Drop Tab */}
          {activeTab === 'drop' && (
            <div className="animate-fadeIn space-y-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Icon.MapPin className="h-4 w-4 text-blue-600" />
                  </span>
                  Delivery Address
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { prop: 'drop_poc', label: 'Contact Person', required: true, type: 'text' },
                    { prop: 'drop_poc_contact_number', label: 'Phone', required: true, type: 'tel' },
                    { prop: 'drop_address', label: 'Address', required: true, type: 'text' },
                    { prop: 'drop_city', label: 'City', required: true, type: 'text' },
                    { prop: 'drop_state', label: 'State', required: true, type: 'text' },
                    { prop: 'drop_poc_email', label: 'Email', required: false, type: 'email' },
                    { prop: 'drop_postal_code', label: 'Postal Code', required: true, type: 'text' },
                    { prop: 'drop_country_code', label: 'Country Code', required: false, type: 'text' },
                  ].map(({ prop, label, required, type }) => (
                    <div key={prop}>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                        {label}
                        {required && <span className="text-rose-600 ml-1">*</span>}
                      </label>
                      <input
                        type={type}
                        name={`drop_details.${prop}`}
                        value={dd[prop] || ''}
                        onChange={handleChange}
                        required={required}
                        className="w-full px-4 py-3 rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-300 hover:ring-slate-300 outline-none bg-white transition-all font-medium"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Packages Tab */}
          {activeTab === 'packages' && (
            <div className="animate-fadeIn space-y-6 max-w-6xl mx-auto">
              {pkgs.map((pkg, pi) => {
                const pkgForm = form.shipment_details?.shipment_packages?.[pi] || pkg || {}
                const isExpanded = expandedPackages.has(pi)
                return (
                  <div
                    key={pi}
                    className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
                  >
                    <button
                      onClick={() => togglePackage(pi)}
                      className="w-full bg-gradient-to-r from-slate-50 to-slate-100/50 hover:from-slate-100 hover:to-slate-150 px-6 py-4 text-left flex justify-between items-center transition-all group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center ring-2 ring-indigo-200/50 group-hover:scale-110 transition-transform">
                          <Icon.Package className="h-5 w-5 text-indigo-600" />
                        </div>
                        <span className="font-bold text-slate-900">Package #{pi + 1}</span>
                      </div>
                      <Icon.ChevronDown className={`h-5 w-5 text-slate-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        isExpanded ? 'max-h-[2000px]' : 'max-h-0'
                      }`}
                    >
                      <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Dimensions */}
                        {[
                          { key: 'product_length', label: 'Length (cm)' },
                          { key: 'product_width', label: 'Width (cm)' },
                          { key: 'product_height', label: 'Height (cm)' },
                        ].map(({ key, label }) => (
                          <div key={key}>
                            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                              {label}
                              <span className="text-rose-600 ml-1">*</span>
                            </label>
                            <input
                              type="number"
                              name={`shipment_details.shipment_packages.${pi}.dimensions.${key}`}
                              value={pkgForm.dimensions?.[key] || ''}
                              onChange={handleChange}
                              min="0.01"
                              step="0.01"
                              required
                              className="w-full px-4 py-3 rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-300 hover:ring-slate-300 outline-none bg-white transition-all font-medium"
                            />
                          </div>
                        ))}
                        {/* Weight */}
                        <div>
                          <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                            Weight (gm)
                            <span className="text-rose-600 ml-1">*</span>
                          </label>
                          <input
                            type="number"
                            name={`shipment_details.shipment_packages.${pi}.weight.product_weight`}
                            value={pkgForm.weight?.product_weight || ''}
                            onChange={handleChange}
                            min="0.01"
                            step="0.01"
                            required
                            className="w-full px-4 py-3 rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-300 hover:ring-slate-300 outline-none bg-white transition-all font-medium"
                          />
                        </div>
                        {/* Items */}
                        <div className="col-span-full mt-4">
                          <h4 className="font-bold text-slate-900 mb-3">Items in Package</h4>
                          {Array.isArray(pkgForm.items) && pkgForm.items.length > 0 ? (
                            pkgForm.items.map((it, ji) => (
                              <div
                                key={ji}
                                className="mb-4 p-4 border border-slate-200 rounded-xl bg-slate-50/50 grid grid-cols-1 md:grid-cols-2 gap-3"
                              >
                                <div>
                                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">SKU</label>
                                  <input
                                    type="text"
                                    name={`shipment_details.shipment_packages.${pi}.items.${ji}.item_general_details.sku`}
                                    value={it.item_general_details?.sku || ''}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2.5 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-300 outline-none bg-white transition-all"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                                    Item Name
                                    <span className="text-rose-600 ml-1">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    name={`shipment_details.shipment_packages.${pi}.items.${ji}.item_general_details.item_name`}
                                    value={it.item_general_details?.item_name || ''}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2.5 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-300 outline-none bg-white transition-all"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                                    Quantity
                                    <span className="text-rose-600 ml-1">*</span>
                                  </label>
                                  <input
                                    type="number"
                                    name={`shipment_details.shipment_packages.${pi}.items.${ji}.quantity`}
                                    value={it.quantity || ''}
                                    onChange={handleChange}
                                    min="1"
                                    step="1"
                                    required
                                    className="w-full px-3 py-2.5 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-300 outline-none bg-white transition-all"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                                    Unit Price
                                    <span className="text-rose-600 ml-1">*</span>
                                  </label>
                                  <input
                                    type="number"
                                    name={`shipment_details.shipment_packages.${pi}.items.${ji}.item_payment_details.unit_price`}
                                    value={it.item_payment_details?.unit_price || ''}
                                    onChange={handleChange}
                                    min="0.01"
                                    step="0.01"
                                    required
                                    className="w-full px-3 py-2.5 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-300 outline-none bg-white transition-all"
                                  />
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-sm italic text-slate-500 text-center py-8 bg-slate-50 rounded-xl">
                              No items in this package
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}

              {/* Fulfilled packages (read-only) */}
              {Array.isArray(form?.shipment_details?.shipment_fulfilled_packages) &&
                form.shipment_details.shipment_fulfilled_packages.length > 0 && (
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200 shadow-sm p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Icon.Check className="h-4 w-4 text-emerald-600" />
                    </span>
                    Fulfilled Packages (Read-Only)
                  </h3>
                  <div className="space-y-3">
                    {form.shipment_details.shipment_fulfilled_packages.map((pkg, idx) => (
                      <div key={idx} className="bg-white rounded-xl p-4 border border-emerald-200">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-slate-900">Package #{idx + 1}</span>
                          {pkg?.waybill && (
                            <span className="px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold">
                              AWB: {pkg.waybill}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-600">
                          Items: <span className="font-semibold">{(pkg.items || []).length || 0}</span> •{" "}
                          Weight: <span className="font-semibold">{pkg?.weight?.product_weight || 0}g</span> •{" "}
                          Dims: <span className="font-semibold">{pkg?.dimensions?.product_length || 0}×
                          {pkg?.dimensions?.product_width || 0}×
                          {pkg?.dimensions?.product_height || 0} {pkg?.dimensions?.unit || ""}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Premium Enhanced Footer */}
        <div className="relative flex justify-between items-center gap-4 px-8 py-6 border-t border-slate-200 bg-gradient-to-b from-white via-slate-50/30 to-slate-50/50">
          {/* Subtle top glow */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
          
          {/* Info section */}
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
            <span className="font-medium">All changes are saved securely</span>
          </div>
          
          {/* Action buttons */}
          <div className="flex items-center gap-3">
            <button 
              onClick={handleClose}
              disabled={saving} 
              className="group relative inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white ring-2 ring-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 hover:ring-slate-300 active:bg-slate-100 font-semibold transition-all duration-200 shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-slate-100/0 via-slate-100/50 to-slate-100/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              <Icon.X className="h-4 w-4 relative z-10" />
              <span className="relative z-10">Cancel</span>
            </button>
            
            <button 
              onClick={handleSubmit}
              disabled={saving} 
              className="group relative inline-flex items-center justify-center gap-2.5 px-8 py-3.5 bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 text-white rounded-xl font-bold transition-all duration-300 shadow-xl shadow-indigo-500/30 hover:shadow-2xl hover:shadow-indigo-500/40 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 overflow-hidden"
            >
              {/* Animated shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              
              {/* Animated gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              <div className="relative z-10 flex items-center gap-2.5">
                {saving && (
                  <div className="relative">
                    <DotLottieReact 
                      src={loaderSrc}
                      loop 
                      autoplay 
                      style={{ width: 22, height: 22 }}
                    />
                  </div>
                )}
                {!saving && (
                  <div className="relative">
                    <Icon.Check className="h-5 w-5 group-hover:scale-110 group-active:scale-95 transition-transform duration-200" />
                    <div className="absolute inset-0 bg-white blur-md opacity-0 group-hover:opacity-50 animate-pulse"></div>
                  </div>
                )}
                <span className="font-bold tracking-wide">
                  {saving ? 'Saving Changes...' : 'Save Changes'}
                </span>
              </div>
              
              {/* Bottom glow effect */}
              <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent"></div>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        /* Premium fade in animation */
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        /* Gradient animation */
        @keyframes gradientMove {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradientMove 8s ease infinite;
        }
        
        /* Shimmer effect */
        @keyframes shimmer {
          from { transform: translateX(-100%); }
          to { transform: translateX(200%); }
        }
        .animate-shimmer {
          animation: shimmer 3s ease-in-out infinite;
        }
        
        /* Slide in animation */
        @keyframes slideIn {
          from { transform: scaleX(0); opacity: 0; }
          to { transform: scaleX(1); opacity: 1; }
        }
        .animate-slideIn {
          transform-origin: left;
          animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        /* Scrollbar styling */
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        /* Premium scrollbar for main content */
        .premium-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .premium-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .premium-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #6366f1, #8b5cf6);
          border-radius: 10px;
          transition: all 0.3s ease;
        }
        .premium-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #4f46e5, #7c3aed);
        }
        
        /* Floating label effect */
        .input-group:focus-within label {
          transform: translateY(-0.5rem) scale(0.9);
          color: #6366f1;
        }
        
        /* Ripple effect on click */
        @keyframes ripple {
          0% {
            transform: scale(0);
            opacity: 0.6;
          }
          100% {
            transform: scale(2.5);
            opacity: 0;
          }
        }
        
        /* Skeleton loading animation */
        @keyframes skeleton-loading {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
        .skeleton {
          background: linear-gradient(
            90deg,
            #f0f0f0 25%,
            #e0e0e0 50%,
            #f0f0f0 75%
          );
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s ease-in-out infinite;
        }
        
        /* Glow effect for active elements */
        @keyframes glow-pulse {
          0%, 100% {
            box-shadow: 0 0 20px rgba(99, 102, 241, 0.3);
          }
          50% {
            box-shadow: 0 0 30px rgba(99, 102, 241, 0.6);
          }
        }
        .glow-pulse {
          animation: glow-pulse 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  )

  // Use React Portal to render modal at document root (outside any stacking context)
  return ReactDOM.createPortal(modalContent, document.body)
}
